import { create } from 'zustand';
import { NotificationOptions, NotificationState } from '../../new-ui-customer-questionnaire/types/notification';

interface NotificationStore {
  notifications: NotificationState[];
  addNotification: (options: NotificationOptions) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

const generateId = () => Math.random().toString(36).substr(2, 9);

const DEFAULT_DURATION = 4000; // 4 seconds

export const useNotificationStore = create<NotificationStore>((set, get) => ({
  notifications: [],

  addNotification: (options: NotificationOptions) => {
    const id = generateId();
    const duration = options.duration !== undefined ? options.duration : DEFAULT_DURATION;

    const notification: NotificationState = {
      ...options,
      id,
      duration,
    };

    set((state) => ({
      notifications: [...state.notifications, notification],
    }));

    // Auto-remove notification after duration
    if (duration > 0) {
      setTimeout(() => {
        get().removeNotification(id);
      }, duration);
    }

    return id;
  },

  removeNotification: (id: string) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearNotifications: () => {
    set({ notifications: [] });
  },
}));

// Helper functions for easy access
export const showNotification = {
  success: (message: string, options?: Omit<NotificationOptions, 'type' | 'message'>) => {
    return useNotificationStore.getState().addNotification({
      type: 'success',
      message,
      ...options,
    });
  },

  error: (message: string, options?: Omit<NotificationOptions, 'type' | 'message'>) => {
    return useNotificationStore.getState().addNotification({
      type: 'error',
      message,
      ...options,
    });
  },

  info: (message: string, options?: Omit<NotificationOptions, 'type' | 'message'>) => {
    return useNotificationStore.getState().addNotification({
      type: 'info',
      message,
      ...options,
    });
  },

  warning: (message: string, options?: Omit<NotificationOptions, 'type' | 'message'>) => {
    return useNotificationStore.getState().addNotification({
      type: 'warning',
      message,
      ...options,
    });
  },
};
